import React, { useState, useEffect, useCallback } from 'react';
import { guestAPI } from '../services/api';

const QuickOrderForm = () => {
  const [reactions, setReactions] = useState([]);
  const [formData, setFormData] = useState({
    reaction_type: 'Rocket',  // Match database format
    quantity: 500,
    dexscreener_link: '',
    pay_currency: 'sol',
    voucher_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [voucherStatus, setVoucherStatus] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [paymentModal, setPaymentModal] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('waiting');
  const [pollingInterval, setPollingInterval] = useState(null);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  
  // Link validation states (same as Dashboard)
  const [validatingLink, setValidatingLink] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [linkReadonly, setLinkReadonly] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractData, setContractData] = useState(null);

  const PRICE_PER_REACTION = 0.05;

  const currencies = [
    { id: 'sol', name: 'SOL (Solana)', icon: 'https://customer-assets.emergentagent.com/job_de3ea956-ca21-4b8c-be57-9719521a3e56/artifacts/cdi9g2n3_sol.svg' },
    { id: 'eth', name: 'ETH (Ethereum)', icon: 'https://customer-assets.emergentagent.com/job_de3ea956-ca21-4b8c-be57-9719521a3e56/artifacts/txv8nw0c_eth.svg' },
    { id: 'bnbbsc', name: 'BNB (BNB Chain)', icon: 'https://customer-assets.emergentagent.com/job_de3ea956-ca21-4b8c-be57-9719521a3e56/artifacts/ktzy8v2w_bnbbsc.svg' },
    { id: 'usdtsol', name: 'USDT (Solana)', icon: 'https://customer-assets.emergentagent.com/job_de3ea956-ca21-4b8c-be57-9719521a3e56/artifacts/bkhb46fx_usdtsol.svg' },
    { id: 'usdcsol', name: 'USDC (Solana)', icon: 'https://customer-assets.emergentagent.com/job_de3ea956-ca21-4b8c-be57-9719521a3e56/artifacts/qq6k837s_usdcsol.svg' }
  ];

  useEffect(() => {
    fetchReactions();
  }, []);

  // Close currency dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCurrencyDropdown && !event.target.closest('.currency-dropdown-container')) {
        setShowCurrencyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCurrencyDropdown]);

  const fetchReactions = async () => {
    try {
      const response = await guestAPI.getReactions();
      if (response.data && response.data.length > 0) {
        // Define desired order: Rocket, Fire, Red Flag, Poop (exclude Favorite/Star)
        const desiredOrder = ['Rocket', 'Fire', 'Red Flag', 'Redflag', 'Poop'];
        const filteredReactions = response.data
          .filter(r => {
            const name = r.name?.toLowerCase() || r.id?.toLowerCase() || '';
            // Exclude Favorite/Star
            return !name.includes('favorite') && !name.includes('star');
          })
          .sort((a, b) => {
            const aName = a.name || a.id || '';
            const bName = b.name || b.id || '';
            const aIndex = desiredOrder.findIndex(n => n.toLowerCase() === aName.toLowerCase());
            const bIndex = desiredOrder.findIndex(n => n.toLowerCase() === bName.toLowerCase());
            return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
          });
        
        setReactions(filteredReactions);
        // Set default to first reaction (Rocket)
        if (filteredReactions.length > 0) {
          setFormData(prev => ({ ...prev, reaction_type: filteredReactions[0].name }));
        }
      }
    } catch (err) {
      console.error('Error fetching reactions:', err);
      // Fallback reactions: Rocket, Fire, Red Flag, Poop (no Favorite)
      setReactions([
        { name: 'Rocket', emoji: '🚀', price_usd: 0.05 },
        { name: 'Fire', emoji: '🔥', price_usd: 0.05 },
        { name: 'Red Flag', emoji: '🚩', price_usd: 0.05 },
        { name: 'Poop', emoji: '💩', price_usd: 0.05 }
      ]);
    }
  };

  // Validate DexScreener link (same as Dashboard)
  const validateDexScreenerLink = async (link) => {
    if (!link || !link.includes('dexscreener.com')) {
      return;
    }

    setValidatingLink(true);
    setTokenInfo(null);
    setError('');
    
    try {
      const cleanLink = link.replace('https://dexscreener.com/', '').replace('http://dexscreener.com/', '');
      const urlParts = cleanLink.split('/').filter(part => part.length > 0);
      
      if (urlParts.length < 2) {
        setError('Invalid DexScreener link format. Expected: https://dexscreener.com/chain/address');
        setValidatingLink(false);
        return;
      }
      
      const chain = urlParts[0];
      const pairId = urlParts[1].split('?')[0]; // Remove query params
      
      const apiUrl = `https://api.dexscreener.com/latest/dex/pairs/${chain}/${pairId}`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const pairResponse = await fetch(apiUrl, { 
        signal: controller.signal,
        headers: { 'Accept': 'application/json' }
      });
      
      clearTimeout(timeoutId);
      
      if (!pairResponse.ok) {
        throw new Error(`API returned status ${pairResponse.status}`);
      }
      
      const pairData = await pairResponse.json();
      
      if (!pairData.pairs || pairData.pairs.length === 0) {
        // This is a token contract address, not a pair address
        setContractData({ chain, pairId });
        setShowContractModal(true);
      } else {
        const pair = pairData.pairs[0];
        setTokenInfo({
          name: pair.baseToken.name,
          symbol: pair.baseToken.symbol,
          address: pair.baseToken.address,
          url: pair.url || link
        });
        setLinkReadonly(true);
      }
    } catch (error) {
      console.error('Error validating DexScreener link:', error);
      if (error.name === 'AbortError') {
        setError('Request timeout. Please try again.');
      } else {
        // For network errors, still allow order creation
        setLinkReadonly(true);
        setTokenInfo({ name: 'Unknown', symbol: '?', address: '', url: link });
      }
    } finally {
      setValidatingLink(false);
    }
  };

  // Convert contract address to pair address
  const handleConvertToPair = async () => {
    if (!contractData) return;
    
    setValidatingLink(true);
    setError('');
    
    try {
      const apiUrl = `https://api.dexscreener.com/latest/dex/tokens/${contractData.pairId}`;
      const tokenResponse = await fetch(apiUrl);
      
      if (!tokenResponse.ok) {
        throw new Error(`API returned status ${tokenResponse.status}`);
      }
      
      const tokenData = await tokenResponse.json();
      
      if (tokenData.pairs && tokenData.pairs.length > 0) {
        let pair = tokenData.pairs.find(p => p.chainId === contractData.chain) || tokenData.pairs[0];
        const newLink = pair.url || `https://dexscreener.com/${pair.chainId}/${pair.pairAddress}`;
        
        setFormData({ ...formData, dexscreener_link: newLink });
        setTokenInfo({
          name: pair.baseToken.name,
          symbol: pair.baseToken.symbol,
          address: pair.baseToken.address,
          url: newLink
        });
        setLinkReadonly(true);
        setShowContractModal(false);
        setContractData(null);
      } else {
        setError('No trading pairs found for this token. Please use a valid Pair Address.');
        setShowContractModal(false);
      }
    } catch (error) {
      setError(`Failed to convert: ${error.message}`);
      setShowContractModal(false);
    } finally {
      setValidatingLink(false);
    }
  };

  const handleLinkChange = (e) => {
    const newLink = e.target.value;
    setFormData({ ...formData, dexscreener_link: newLink });
    setTokenInfo(null);
    setLinkReadonly(false);
    
    if (newLink.includes('dexscreener.com/') && newLink.split('/').length >= 5) {
      validateDexScreenerLink(newLink);
    }
  };

  const handleResetLink = () => {
    setFormData({ ...formData, dexscreener_link: '' });
    setTokenInfo(null);
    setLinkReadonly(false);
    setError('');
  };

  const calculateTotal = () => {
    const baseTotal = formData.quantity * PRICE_PER_REACTION;
    return baseTotal - discount;
  };

  const handleQuantityChange = (value) => {
    let qty = parseInt(value) || 500;
    qty = Math.max(500, Math.min(10000, qty));
    setFormData({ ...formData, quantity: qty });
    setVoucherStatus(null);
    setDiscount(0);
  };

  const validateVoucher = async () => {
    if (!formData.voucher_code) return;
    
    try {
      const response = await guestAPI.validateVoucher({
        code: formData.voucher_code,
        order_total: formData.quantity * PRICE_PER_REACTION
      });
      
      setVoucherStatus({ valid: true, ...response.data });
      setDiscount(response.data.discount_amount);
    } catch (err) {
      setVoucherStatus({ 
        valid: false, 
        message: err.response?.data?.detail || 'Invalid voucher' 
      });
      setDiscount(0);
    }
  };

  const pollPaymentStatus = useCallback(async (orderId) => {
    try {
      const response = await guestAPI.getOrderStatus(orderId);
      const status = response.data.payment_status;
      const actuallyPaid = response.data.actually_paid;
      const payAmount = response.data.pay_amount;
      const payCurrency = response.data.pay_currency;
      
      setPaymentStatus(status);
      
      // Only mark as success if payment is actually finished (paid)
      if (status === 'finished') {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        setPaymentModal(prev => ({ ...prev, paymentComplete: true }));
      } else if (status === 'partially_paid') {
        // Update payment modal with partial payment info
        const shortage = payAmount - actuallyPaid;
        setPaymentModal(prev => ({ 
          ...prev, 
          isPartiallyPaid: true,
          actuallyPaid: actuallyPaid,
          shortage: shortage,
          payCurrency: payCurrency
        }));
        // Keep polling - admin might process manually
      } else if (status === 'failed' || status === 'expired') {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      }
      // "confirmed", "waiting", "confirming", "sending" - keep polling
    } catch (err) {
      console.error('Error polling status:', err);
    }
  }, [pollingInterval]);

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate link
    if (!formData.dexscreener_link.includes('dexscreener.com/')) {
      setError('Please enter a valid DexScreener link');
      setLoading(false);
      return;
    }

    // Check if token info is validated
    if (!tokenInfo) {
      setError('Please wait for link validation to complete');
      setLoading(false);
      return;
    }

    try {
      const response = await guestAPI.createOrder({
        reaction_type: formData.reaction_type,
        quantity: formData.quantity,
        dexscreener_link: tokenInfo.url || formData.dexscreener_link,
        pay_currency: formData.pay_currency,
        voucher_code: formData.voucher_code || null
      });

      setPaymentModal(response.data);
      setPaymentStatus('waiting');

      const interval = setInterval(() => {
        pollPaymentStatus(response.data.order_id);
      }, 10000);
      setPollingInterval(interval);

    } catch (err) {
      // Handle error - detail can be string or array of validation errors
      let errorMessage = 'Failed to create order. Please try again.';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors are returned as array
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const closePaymentModal = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    
    const wasComplete = paymentModal?.paymentComplete;
    setPaymentModal(null);
    
    if (wasComplete) {
      // Reset to first reaction or default
      const defaultReaction = reactions.length > 0 ? reactions[0].name : 'Rocket';
      setFormData({
        reaction_type: defaultReaction,
        quantity: 500,
        dexscreener_link: '',
        pay_currency: 'sol',
        voucher_code: ''
      });
      setDiscount(0);
      setVoucherStatus(null);
      setTokenInfo(null);
      setLinkReadonly(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'finished':
      case 'confirmed':
        return 'text-green-400';
      case 'confirming':
      case 'sending':
        return 'text-yellow-400';
      case 'waiting':
        return 'text-blue-400';
      case 'partially_paid':
        return 'text-orange-400';
      case 'failed':
      case 'expired':
      case 'refunded':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'finished':
      case 'confirmed':
        return '✅ Payment Confirmed!';
      case 'confirming':
        return '⏳ Confirming payment...';
      case 'sending':
        return '📤 Processing...';
      case 'waiting':
        return '⏱️ Waiting for payment...';
      case 'partially_paid':
        return '⚠️ Partial payment received';
      case 'failed':
        return '❌ Payment failed';
      case 'expired':
        return '⏰ Payment expired';
      case 'refunded':
        return '↩️ Refunded';
      default:
        return status;
    }
  };

  return (
    <>
      <div className="bg-slate-800/80 backdrop-blur-md rounded-2xl p-6 border border-purple-500/30 shadow-xl">
        <h3 className="text-xl font-bold mb-4 text-center bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          🚀 Quick Order - No Login Required
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Reaction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select Reaction
            </label>
            <div className="grid grid-cols-4 gap-2">
              {reactions.map((reaction) => (
                <button
                  key={reaction.name}
                  type="button"
                  onClick={() => setFormData({ ...formData, reaction_type: reaction.name })}
                  className={`p-3 rounded-lg text-2xl transition-all ${
                    formData.reaction_type === reaction.name
                      ? 'bg-purple-600 ring-2 ring-purple-400'
                      : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                  title={reaction.name}
                >
                  {reaction.emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Quantity
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleQuantityChange(formData.quantity - 1)}
                className="px-3 py-2 bg-slate-700 rounded-lg hover:bg-slate-600"
              >
                -
              </button>
              <input
                type="number"
                value={formData.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                min="1"
                max="10000"
                step="1"
                className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-center"
              />
              <button
                type="button"
                onClick={() => handleQuantityChange(formData.quantity + 1)}
                className="px-3 py-2 bg-slate-700 rounded-lg hover:bg-slate-600"
              >
                +
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Total: ${(formData.quantity * PRICE_PER_REACTION).toFixed(2)}
            </p>
          </div>

          {/* DexScreener Link with validation */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              DexScreener Link
            </label>
            <div className="relative">
              <input
                type="url"
                value={formData.dexscreener_link}
                onChange={handleLinkChange}
                placeholder="https://dexscreener.com/solana/..."
                disabled={linkReadonly}
                className={`w-full px-4 py-2 bg-slate-700 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10 ${
                  linkReadonly ? 'border-green-500 bg-green-900/20' : 'border-slate-600'
                } ${validatingLink ? 'opacity-70' : ''}`}
              />
              {validatingLink && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <svg className="animate-spin h-5 w-5 text-purple-400" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                </div>
              )}
              {linkReadonly && (
                <button
                  type="button"
                  onClick={handleResetLink}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              )}
            </div>
            
            {/* Token Info Display */}
            {tokenInfo && (
              <div className="mt-2 p-2 bg-green-900/30 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span className="text-sm text-white font-medium">{tokenInfo.name}</span>
                  <span className="text-xs text-gray-400">({tokenInfo.symbol})</span>
                </div>
              </div>
            )}
          </div>

          {/* Payment Currency - Custom Dropdown with Images */}
          <div className="currency-dropdown-container">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Pay with
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white flex items-center justify-between cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <img 
                    src={currencies.find(c => c.id === formData.pay_currency)?.icon} 
                    alt="" 
                    className="w-6 h-6"
                  />
                  <span>{currencies.find(c => c.id === formData.pay_currency)?.name}</span>
                </div>
                <svg 
                  className={`w-5 h-5 text-gray-400 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showCurrencyDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl">
                  {currencies.map((currency) => (
                    <button
                      key={currency.id}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, pay_currency: currency.id });
                        setShowCurrencyDropdown(false);
                      }}
                      className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-purple-500/20 transition-colors text-left ${
                        formData.pay_currency === currency.id ? 'bg-purple-500/30' : ''
                      }`}
                    >
                      <img src={currency.icon} alt="" className="w-6 h-6" />
                      <span className="text-white">{currency.name}</span>
                      {formData.pay_currency === currency.id && (
                        <svg className="w-5 h-5 text-green-400 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Voucher Code */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Promo Code (Optional)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.voucher_code}
                onChange={(e) => {
                  setFormData({ ...formData, voucher_code: e.target.value.toUpperCase() });
                  setVoucherStatus(null);
                  setDiscount(0);
                }}
                placeholder="Enter code"
                className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg uppercase"
              />
              <button
                type="button"
                onClick={validateVoucher}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-sm"
              >
                Apply
              </button>
            </div>
            {voucherStatus && (
              <p className={`text-xs mt-1 ${voucherStatus.valid ? 'text-green-400' : 'text-red-400'}`}>
                {voucherStatus.valid 
                  ? `✓ -$${voucherStatus.discount_amount.toFixed(2)} discount applied!`
                  : `✗ ${voucherStatus.message}`
                }
              </p>
            )}
          </div>

          {/* Total */}
          <div className="bg-slate-700/50 rounded-lg p-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Subtotal:</span>
              <span>${(formData.quantity * PRICE_PER_REACTION).toFixed(2)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm mb-2 text-green-400">
                <span>Discount:</span>
                <span>-${discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold border-t border-slate-600 pt-2">
              <span>Total:</span>
              <span className="text-purple-400">${calculateTotal().toFixed(2)}</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-sm text-red-400">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !tokenInfo}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Creating Order...
              </span>
            ) : !tokenInfo && formData.dexscreener_link ? (
              'Validating Link...'
            ) : (
              '💳 Pay Now'
            )}
          </button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-4">
          Secure payment via NOWPayments • No account needed
        </p>
      </div>

      {/* Contract Address Modal */}
      {showContractModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-yellow-500/30">
            <div className="text-center mb-4">
              <div className="text-4xl mb-2">⚠️</div>
              <h3 className="text-xl font-bold text-yellow-400">Contract Address Detected</h3>
            </div>
            <p className="text-gray-300 mb-4 text-center">
              You entered a token contract address. We need to convert it to a trading pair address for reactions to work.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowContractModal(false)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleConvertToPair}
                disabled={validatingLink}
                className="flex-1 py-3 bg-yellow-600 hover:bg-yellow-700 rounded-lg font-semibold transition-all disabled:opacity-50"
              >
                {validatingLink ? 'Converting...' : 'Convert to Pair'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-slate-800 rounded-2xl p-5 max-w-sm w-full border border-purple-500/30 max-h-[85vh] overflow-y-auto">
            {paymentModal.paymentComplete ? (
              <div className="text-center">
                <div className="text-5xl mb-3">🎉</div>
                <h3 className="text-xl font-bold text-green-400 mb-3">Payment Successful!</h3>
                <p className="text-gray-300 text-sm mb-4">
                  Order received and being processed.
                </p>
                <div className="bg-slate-700/50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-400">Order ID</p>
                  <p className="font-mono text-sm text-purple-400">{paymentModal.order_id}</p>
                </div>
                <a
                  href={`/order/${paymentModal.order_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-2 mb-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold text-sm text-center"
                >
                  📋 Check Order Status
                </a>
                <button
                  onClick={closePaymentModal}
                  className="w-full py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold text-sm"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold">Complete Payment</h3>
                  <button
                    onClick={closePaymentModal}
                    className="text-gray-400 hover:text-white text-xl"
                  >
                    ✕
                  </button>
                </div>

                {/* Status */}
                <div className="text-center mb-3">
                  <p className={`text-sm font-semibold ${getStatusColor(paymentStatus)}`}>
                    {getStatusText(paymentStatus)}
                  </p>
                </div>

                {/* QR Code - Centered */}
                <div className="flex justify-center mb-3">
                  <img
                    src={guestAPI.getPaymentQR(paymentModal.payment_id)}
                    alt="Payment QR Code"
                    className="w-32 h-32 bg-white rounded-lg p-1"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>

                {/* Amount to send */}
                <div className="bg-slate-700/50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-gray-400 mb-1">Send exactly:</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {currencies.find(c => c.id === paymentModal.pay_currency?.toLowerCase() || c.id === paymentModal.pay_currency)?.icon && (
                        <img 
                          src={currencies.find(c => c.id === paymentModal.pay_currency?.toLowerCase() || c.id === paymentModal.pay_currency)?.icon}
                          alt=""
                          className="w-5 h-5"
                        />
                      )}
                      <span className="font-mono text-lg text-purple-400">
                        {paymentModal.pay_amount} {paymentModal.pay_currency?.toUpperCase()}
                      </span>
                    </div>
                    <button
                      onClick={() => {navigator.clipboard.writeText(paymentModal.pay_amount?.toString()); alert('Copied!')}}
                      className="text-xs text-purple-400 hover:text-purple-300 px-2 py-1 bg-slate-800 rounded"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-slate-700/50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-gray-400 mb-1">To address:</p>
                  <p className="font-mono text-xs text-gray-300 break-all mb-2">
                    {paymentModal.pay_address}
                  </p>
                  <button
                    onClick={() => {navigator.clipboard.writeText(paymentModal.pay_address); alert('Address copied!')}}
                    className="text-xs text-purple-400 hover:text-purple-300"
                  >
                    📋 Copy Address
                  </button>
                </div>

                {/* Order Summary */}
                <div className="bg-slate-700/30 rounded-lg p-2 mb-3 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Quantity:</span>
                    <span>{paymentModal.quantity} reactions</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Total:</span>
                    <span className="text-green-400">${paymentModal.total_usd?.toFixed(2)}</span>
                  </div>
                </div>

                {/* Check Order Link */}
                <a
                  href={`/order/${paymentModal.order_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-2 mb-3 bg-slate-700 hover:bg-slate-600 rounded-lg font-semibold text-xs text-center text-purple-400"
                >
                  🔗 Open order status page
                </a>

                <p className="text-xs text-gray-500 text-center">
                  Status updates automatically. Please wait...
                </p>

                {/* Partial Payment Warning */}
                {paymentStatus === 'partially_paid' && paymentModal.isPartiallyPaid && (
                  <div className="mt-3 bg-orange-500/20 border border-orange-500/50 rounded-lg p-3">
                    <h4 className="font-bold text-orange-400 text-sm mb-2">⚠️ Insufficient Payment!</h4>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Received:</span>
                        <span className="text-orange-400 font-mono">
                          {paymentModal.actuallyPaid} {paymentModal.payCurrency?.toUpperCase() || paymentModal.pay_currency?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Shortage:</span>
                        <span className="text-red-400 font-mono">
                          {paymentModal.shortage?.toFixed(8)} {paymentModal.payCurrency?.toUpperCase() || paymentModal.pay_currency?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Admin has been notified. Send the remaining amount to the same address.
                    </p>
                  </div>
                )}

                {(paymentStatus === 'failed' || paymentStatus === 'expired') && (
                  <button
                    onClick={closePaymentModal}
                    className="w-full mt-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold text-sm"
                  >
                    Close
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default QuickOrderForm;
